import React, { CSSProperties, ReactChild, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Dropdown.module.scss';
import DropdownWrapper from 'react-dropdown-wrapper';
import { DropdownWrapperChildFunc } from '../../types/ReactDropdownWrapper';

type Props = {
    style?: CSSProperties;
    children: ReactChild | ReactChild[];
    options: React.ReactNode | React.ReactNode[];
};

const Dropdown: React.FC<Props> = ({ children, options, style }: Props) => {
    const iconRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<CSSProperties>({});
    const [isInActionColumn, setIsInActionColumn] = useState(false);
    const [internalIsShow, setInternalIsShow] = useState(false);

    const updatePosition = () => {
        if (iconRef.current && isInActionColumn) {
            const rect = iconRef.current.getBoundingClientRect();
            setDropdownPosition({
                position: 'fixed',
                top: `${rect.top}px`,
                left: `${rect.left - 5}px`,
                transform: 'translateX(-100%)',
                zIndex: 10000,
                minWidth: '110px',
            });
        }
    };

    useEffect(() => {
        if (iconRef.current) {
            const actionColumn = iconRef.current.closest('.global-action-column');
            const isInAction = !!actionColumn;
            setIsInActionColumn(isInAction);
        }
    }, []);

    useEffect(() => {
        if (isInActionColumn && internalIsShow) {
            updatePosition();

            const handleScroll = () => {
                setInternalIsShow(false);
            };

            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', handleScroll, true);
            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isInActionColumn, internalIsShow]);

    // Handle click outside for Portal rendering
    useEffect(() => {
        if (!isInActionColumn || !internalIsShow) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                iconRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                !iconRef.current.contains(event.target as Node)
            ) {
                setInternalIsShow(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isInActionColumn, internalIsShow]);

    const renderDropdown = (isShow: boolean) => {
        const shouldShow = isInActionColumn ? internalIsShow : isShow;

        if (!shouldShow) return null;

        const dropdownElement = (
            <div
                ref={dropdownRef}
                className={styles.dropdownContainer}
                style={isInActionColumn ? { ...dropdownPosition, ...style } : style}
                onClick={e => e.stopPropagation()}
            >
                {options}
            </div>
        );

        if (isInActionColumn) {
            return ReactDOM.createPortal(dropdownElement, document.body);
        }

        return dropdownElement;
    };

    return (
        <DropdownWrapper closeOnOutsideClick={!isInActionColumn}>
            {({ changeStatus, isShow }: DropdownWrapperChildFunc) => (
                <>
                    <div
                        ref={iconRef}
                        className={styles.icon}
                        onClick={e => {
                            if (isInActionColumn) {
                                e.preventDefault();
                                e.stopPropagation();
                                updatePosition();
                                setInternalIsShow(!internalIsShow);
                            } else {
                                changeStatus(!isShow);
                            }
                        }}
                    >
                        {children}
                    </div>
                    {renderDropdown(isShow)}
                </>
            )}
        </DropdownWrapper>
    );
}

export default Dropdown;
